import React, { useCallback, useEffect, useRef, useState } from "react";

import HttpManager from "../../../../services/HttpManager";
import { ArticleDTO } from "../../../../models/ArticleDTO";
import { kitDeliveryStatus, owedArticleStatus } from "../../../../constants";
import DeliveryPrompt, { DeliveryAction } from "../../../../components/kits/deliveryPrompt";
import KitHistoryModal from "../../../../components/kits/kitHistoryModal";
import {
	AdvisorsCell, Empty, EnrollmentCell, FilterBar, KitFilters, Loading, PER_PAGE_DEFAULT, Pager, PaidDatesCell, PaymentCell, ProductCell, SortTh, StatusCell, StudentCell,
	apiError, bestEnrollment, daysAgo, saveExcel, today, ui, useCampus, useDebounced, useFilterOptions, useIdleLocked, usePerms
} from "./shared";

/* Tienda → Control de Entregas (section "kits"): Por Entregar · Vendidos sin Inventario · Entregados */

type TabKey = "por_entregar" | "sin_inventario" | "entregados";
const TABS: { key: TabKey; label: string; tab: string }[] = [
	{ key: "por_entregar", label: "Por Entregar", tab: "paid_undelivered" },
	{ key: "sin_inventario", label: "Vendidos sin Inventario", tab: "undelivered" },
	{ key: "entregados", label: "Entregados", tab: "delivered" }
];
const SECTION = "kits";
/*
	Por Entregar y Vendidos sin Inventario no llevan fecha por defecto: un kit pagado hace más de 90 días y sin entregar
	también se debe. Para no llenarlas con los miles de kits viejos "Sin Registro" (vendidos antes de sep-2026), abren con
	Status = Sin Entregar + Entrega Parcial; "Sin Registro" se elige en el filtro Status. Entregados abre con los últimos
	90 días (es historial y crece sin fin).
*/
const OWED_STATUSES = [String(kitDeliveryStatus.PENDING), String(kitDeliveryStatus.PARTIAL)];
const defaultFilters = (key: TabKey): KitFilters => key === "entregados"
	? { paidFrom: daysAgo(90), sort: "lastPaid", dir: "desc" }
	: { deliveryStatusIds: OWED_STATUSES, sort: "lastPaid", dir: "desc" };

const ControlEntregas: React.FC = () => {
	const { campusId } = useCampus();
	const idleLocked = useIdleLocked();
	const perms: any = usePerms(SECTION);
	const canDeliver = !!perms.create;
	const canFix = !!perms.update;
	const options = useFilterOptions(campusId, SECTION);
	const http = HttpManager.getInstance();

	const [tabKey, setTabKey] = useState<TabKey>("por_entregar");
	const tab = TABS.find((t) => t.key === tabKey) || TABS[0];
	const [filters, setFilters] = useState<KitFilters>(defaultFilters("por_entregar"));
	const debouncedQ = useDebounced(filters.q || "");
	const [page, setPage] = useState<number>(1);
	const [perPage, setPerPage] = useState<number>(PER_PAGE_DEFAULT);
	const [rows, setRows] = useState<ArticleDTO[]>([]);
	const [groups, setGroups] = useState<any[]>([]);
	const [openGroup, setOpenGroup] = useState<number | null>(null);
	const [groupRows, setGroupRows] = useState<Record<number, ArticleDTO[]>>({});
	const [total, setTotal] = useState<number>(0);
	const [lastPage, setLastPage] = useState<number>(1);
	const [loading, setLoading] = useState<boolean>(false);
	const [error, setError] = useState<string | null>(null);
	const [counts, setCounts] = useState<Record<string, number>>({});
	const [exporting, setExporting] = useState<boolean>(false);
	const [deliveryKit, setDeliveryKit] = useState<ArticleDTO | null>(null);
	const [deliveryBusy, setDeliveryBusy] = useState<boolean>(false);
	const [deliveryError, setDeliveryError] = useState<string | null>(null);
	const [historyKit, setHistoryKit] = useState<ArticleDTO | null>(null);
	/** Only the latest request may paint the table (a slow answer of the previous tab/filter must not overwrite it). */
	const requestRef = useRef<number>(0);

	const effective = useCallback((): KitFilters => ({ ...filters, q: debouncedQ.trim() }), [filters, debouncedQ]);

	const load = useCallback(async () => {
		if (!campusId) return;
		const request = ++requestRef.current;
		setLoading(true);
		setError(null);
		try {
			if (tabKey === "sin_inventario") {
				const response = await http.kitsService.withoutInventory(campusId, SECTION, effective());
				if (request !== requestRef.current) return;
				setGroups(response.data || []);
				setTotal((response.data || []).length);
				setLastPage(1);
				setOpenGroup(null);
				setGroupRows({});
			} else {
				const response = await http.kitsService.list(campusId, SECTION, tab.tab, effective(), page, perPage);
				if (request !== requestRef.current) return;
				setRows(response.data.data || []);
				setTotal(response.data.total || 0);
				setLastPage(response.data.last_page || 1);
			}
		} catch (requestError: any) {
			if (request !== requestRef.current) return;
			setError(apiError(requestError, "No se pudo cargar la información."));
			setRows([]);
			setGroups([]);
		} finally {
			if (request === requestRef.current) setLoading(false);
		}
	}, [campusId, tabKey, tab.tab, effective, page, perPage]);

	useEffect(() => { load(); }, [load]);

	const refreshCounts = useCallback(() => {
		if (!campusId) return;
		TABS.filter((t) => t.key !== "sin_inventario").forEach((t) => {
			const base = defaultFilters(t.key);
			http.kitsService.list(campusId, SECTION, t.tab, { paidFrom: base.paidFrom, deliveryStatusIds: base.deliveryStatusIds }, 1, 1)
				.then((response) => setCounts((previous) => ({ ...previous, [t.key]: response.data.total || 0 })))
				.catch(() => undefined);
		});
	}, [campusId]);
	useEffect(() => { refreshCounts(); }, [refreshCounts]);

	const patch = (changes: Partial<KitFilters>) => { setFilters((previous) => ({ ...previous, ...changes })); setPage(1); };
	const onSort = (key: string) => patch({ sort: key, dir: filters.sort === key && filters.dir === "desc" ? "asc" : "desc" });
	const switchTab = (key: TabKey) => {
		// Date window and Status belong to the tab (Entregados has no Status filter): those two take the new tab's defaults.
		const next = defaultFilters(key);
		setFilters((previous) => ({ ...previous, paidFrom: next.paidFrom, deliveryStatusIds: next.deliveryStatusIds }));
		setTabKey(key); setPage(1); setRows([]);
	};

	const afterChange = async () => { await load(); refreshCounts(); if (openGroup !== null) loadGroup(openGroup, true); };

	const onDelivery = async (action: DeliveryAction, note: string, warehouseId?: number | null) => {
		if (!deliveryKit) return;
		setDeliveryBusy(true);
		setDeliveryError(null);
		try {
			if (action.kind === "items") await http.inventoryService.registerDeliveryItems(campusId, deliveryKit.id, action.items, note, warehouseId);
			else if (action.kind === "delivery") await http.kitsService.registerDelivery(campusId, deliveryKit.id, action.type, note);
			else if (action.kind === "note") await http.kitsService.addNote(campusId, deliveryKit.id, note);
			else await http.kitsService.setDeliveryStatus(campusId, deliveryKit.id, action.deliveryStatusId, note);
			setDeliveryKit(null);
			await afterChange();
		} catch (requestError: any) {
			setDeliveryError(apiError(requestError, "No se pudo registrar la entrega."));
		} finally {
			setDeliveryBusy(false);
		}
	};

	const loadGroup = async (articleId: number, force = false) => {
		if (!force && groupRows[articleId]) return;
		try {
			const response = await http.kitsService.list(campusId, SECTION, "undelivered", { ...effective(), articleIds: [String(articleId)] }, 1, 500);
			setGroupRows((previous) => ({ ...previous, [articleId]: response.data.data || [] }));
		} catch (requestError) {
			setGroupRows((previous) => ({ ...previous, [articleId]: [] }));
		}
	};
	const toggleGroup = (articleId: number) => { if (openGroup === articleId) { setOpenGroup(null); return; } setOpenGroup(articleId); loadGroup(articleId); };

	const exportExcel = async () => {
		setExporting(true);
		try {
			await saveExcel(campusId, SECTION, tabKey === "sin_inventario" ? "inventory" : "kits", tab.tab, effective(), `entregas_${tabKey}_${today()}`);
		} catch (requestError) {
			setError(apiError(requestError, "No se pudo generar el Excel."));
		} finally {
			setExporting(false);
		}
	};

	const actions = (kit: ArticleDTO) => {
		const deliverable = !kit.isCancelled && (kit.statusId === owedArticleStatus.FULLY_PAID || kit.statusId === owedArticleStatus.PARTLY_PAID) && kit.deliveryStatusId !== kitDeliveryStatus.DELIVERED;
		const unknown = !kit.isCancelled && kit.deliveryStatusId === kitDeliveryStatus.UNKNOWN;
		return <div style={{ display: "flex", gap: "6px", whiteSpace: "nowrap" }}>
			<button type="button" className={ui.btn} disabled={idleLocked} onClick={() => setHistoryKit(kit)}>Historial</button>
			{(canDeliver && deliverable) || (canFix && unknown)
				? <button type="button" className={ui.btnPrimary} disabled={idleLocked} onClick={() => { setDeliveryError(null); setDeliveryKit(kit); }}>Registrar Entrega</button>
				: null}
		</div>;
	};

	const kitTable = (data: ArticleDTO[], paidOnly: boolean, sortable: boolean) => <table style={{ width: "100%", borderCollapse: "collapse" }}>
		<thead><tr>
			{sortable ? <SortTh label="Alumno" sortKey="student" sort={filters.sort} dir={filters.dir} onSort={onSort} /> : <th style={ui.th}>Alumno</th>}
			{sortable ? <SortTh label="Producto" sortKey="product" sort={filters.sort} dir={filters.dir} onSort={onSort} /> : <th style={ui.th}>Producto</th>}
			<th style={ui.th}>Curso · Horario · Profesor</th>
			{sortable ? <SortTh label="Último Pago" sortKey="lastPaid" sort={filters.sort} dir={filters.dir} onSort={onSort} /> : <th style={ui.th}>Último Pago</th>}
			{sortable ? <SortTh label={paidOnly ? "Pagado" : "Pagado / Total"} sortKey="amount" align="right" sort={filters.sort} dir={filters.dir} onSort={onSort} /> : <th style={{ ...ui.th, ...ui.right }}>{paidOnly ? "Pagado" : "Pagado / Total"}</th>}
			<th style={{ ...ui.th, ...ui.center }}>Status</th>
			<th style={ui.th}>Vendido por</th>
			<th style={ui.th}></th>
		</tr></thead>
		<tbody>{data.map((kit) => <tr key={kit.id}>
			<td style={ui.td}><StudentCell kit={kit} /></td>
			<td style={ui.td}><ProductCell kit={kit} /></td>
			<td style={ui.td}><EnrollmentCell enrollment={bestEnrollment(kit)} extra={(kit.enrollments || []).length - 1} /></td>
			<td style={ui.td}><PaidDatesCell kit={kit} /></td>
			<td style={ui.td}><PaymentCell kit={kit} paidOnly={paidOnly && !kit.isDebt} /></td>
			<td style={{ ...ui.td, ...ui.center }}><StatusCell kit={kit} /></td>
			<td style={ui.td}><AdvisorsCell kit={kit} /></td>
			<td style={ui.td}>{actions(kit)}</td>
		</tr>)}</tbody>
	</table>;

	const inventoryTable = () => <table style={{ width: "100%", borderCollapse: "collapse" }}>
		<thead><tr>
			<th style={ui.th}></th>
			<th style={ui.th}>Producto</th>
			<th style={{ ...ui.th, ...ui.right }}>En inventario</th>
			<th style={{ ...ui.th, ...ui.right }}>Por entregar</th>
			<th style={{ ...ui.th, ...ui.right }}>Alumnos</th>
			<th style={{ ...ui.th, ...ui.right }}>Pagados / Con abono</th>
			<th style={{ ...ui.th, ...ui.right }}>Faltan por comprar</th>
			<th style={ui.th}>Pago más antiguo</th>
		</tr></thead>
		<tbody>{groups.map((group) => <React.Fragment key={group.articleId}>
			<tr onClick={() => toggleGroup(group.articleId)} style={{ cursor: "pointer", background: openGroup === group.articleId ? "var(--wa-acento-suave)" : undefined }}>
				<td style={{ ...ui.td, width: "24px", color: "var(--wa-apagado)" }}>{openGroup === group.articleId ? "▾" : "▸"}</td>
				<td style={ui.td}><div style={{ fontWeight: 600 }}>{group.name}</div><div style={ui.tiny}>SKU {group.sku}</div></td>
				<td style={{ ...ui.td, ...ui.right }}>{group.stock}</td>
				<td style={{ ...ui.td, ...ui.right }}><b>{group.units}</b>{group.units !== group.kits ? <div style={ui.small}>{group.kits} kits</div> : null}</td>
				<td style={{ ...ui.td, ...ui.right }}>{group.students}</td>
				<td style={{ ...ui.td, ...ui.right }}>{group.paidFull} / {group.partial}</td>
				<td style={{ ...ui.td, ...ui.right, color: group.shortage > 0 ? "var(--wa-oro)" : "var(--wa-verde)", fontWeight: 700 }}>{group.shortage > 0 ? group.shortage : "Completo"}</td>
				<td style={{ ...ui.td, whiteSpace: "nowrap" }}>{group.oldestPaid || "—"}</td>
			</tr>
			{openGroup === group.articleId ? <tr><td colSpan={8} style={{ padding: "0 0 8px 34px", background: "var(--wa-papel-2)" }}>
				{groupRows[group.articleId] ? (groupRows[group.articleId].length ? kitTable(groupRows[group.articleId], false, false) : <Empty />) : <Loading />}
			</td></tr> : null}
		</React.Fragment>)}</tbody>
	</table>;

	if (!campusId) return <div style={ui.page}>Selecciona un plantel.</div>;

	return <div style={ui.page}>
		<h2 style={ui.title}>Control de Entregas</h2>
		<div style={ui.tabs}>
			{TABS.map((t) => <button key={t.key} type="button" disabled={idleLocked} className={"btn rounded-pill " + (t.key === tabKey ? "btn-primary" : "btn-outline-secondary")} onClick={() => switchTab(t.key)}>
				{t.label}{counts[t.key] !== undefined ? <span style={ui.badge(t.key === tabKey)}>{counts[t.key]}</span> : null}
			</button>)}
		</div>
		<FilterBar options={options} filters={filters} busy={idleLocked} perPage={perPage}
			show={{ search: true, course: true, period: true, schedule: true, mentor: true, weekday: true, article: true, delivery: tabKey !== "entregados", firstPaidRange: true }}
			onChange={patch} onReset={() => { setFilters(defaultFilters(tabKey)); setPage(1); }} defaults={defaultFilters(tabKey)}
			onPerPage={tabKey === "sin_inventario" ? undefined : (size) => { setPerPage(size); setPage(1); }} onExport={exportExcel} exporting={exporting} exportDisabled={total < 1} />
		{tabKey !== "sin_inventario" ? <Pager page={page} lastPage={lastPage} total={total} unit="kits" busy={loading} onPage={setPage} /> : <div style={{ height: "8px" }} />}
		<div style={{ ...ui.card, overflowX: "auto" }}>
			{loading ? <Loading /> : error ? <div className="alert alert-danger" style={{ margin: "16px" }}>{error}</div>
				: tabKey === "sin_inventario" ? (groups.length ? inventoryTable() : <Empty text="Nada pendiente de entregar con estos filtros." />)
				: rows.length ? kitTable(rows, tabKey === "por_entregar", true) : <Empty />}
		</div>
		{tabKey !== "sin_inventario" && lastPage > 1 ? <Pager page={page} lastPage={lastPage} total={total} unit="kits" busy={loading} onPage={setPage} /> : null}

		{deliveryKit ? <DeliveryPrompt kit={deliveryKit} campusId={campusId} busy={deliveryBusy} error={deliveryError} canCreate={canDeliver} canUpdate={canFix} onConfirm={onDelivery} onCancel={() => { if (!deliveryBusy) setDeliveryKit(null); }} /> : null}
		{historyKit ? <KitHistoryModal campusId={campusId} kitId={historyKit.id} title={"Historial · " + (historyKit.name || historyKit.title)} subtitle={(historyKit.studentName || "") + (historyKit.studentCode ? " · " + historyKit.studentCode : "")} onClose={() => setHistoryKit(null)} /> : null}
	</div>;
};

export default ControlEntregas;
